import { z } from "zod";

const PAGE_SIZE = 10;

/** Sortable list columns (story: name, type, assignment type, priority,
 *  status). Name and type map to the API's per-language columns. */
export const ALERT_SORT_COLUMNS = [
  "name",
  "type",
  "targetType",
  "priority",
  "status",
] as const;

export type AlertSortColumn = (typeof ALERT_SORT_COLUMNS)[number];

export const alertsSearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  sortBy: z.enum(ALERT_SORT_COLUMNS).optional(),
  sort: z.enum(["asc", "desc"]).optional(),
  // No `.trim()` -- the search input is controlled by router state, so
  // trimming on parse would strip a trailing space the instant it's typed.
  search: z.string().min(1).optional(),
  /** Alert Type -- a category id. */
  categoryId: z.string().min(1).optional(),
  targetType: z.enum(["ASSET_TYPE", "ASSET", "DEVICE_TYPE", "DEVICE", "GEOFENCE"]).optional(),
  priority: z.enum(["VERY_HIGH", "HIGH", "MEDIUM", "LOW"]).optional(),
  status: z.enum(["ACTIVE", "INACTIVE"]).optional(),
});

export type AlertsSearch = z.infer<typeof alertsSearchSchema>;

/** Alert History's sortable columns -- the API's own `sortBy` values. The
 *  value and location columns can't be sorted server-side. */
export const ALERT_HISTORY_SORT_COLUMNS = [
  "occurredAt",
  "company",
  "asset",
  "alertType",
  "alertName",
] as const;

export type AlertHistorySortColumn =
  (typeof ALERT_HISTORY_SORT_COLUMNS)[number];

/** A calendar day, `yyyy-MM-dd`, in the user's own time zone. */
const day = z.string().regex(/^\d{4}-\d{2}-\d{2}$/);

export const alertHistorySearchSchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(PAGE_SIZE),
  // Unset means the default order: newest first (Scenario 34).
  sortBy: z.enum(ALERT_HISTORY_SORT_COLUMNS).optional(),
  sort: z.enum(["asc", "desc"]).optional(),
  search: z.string().min(1).optional(),
  accountId: z.string().min(1).optional(),
  assetId: z.string().min(1).optional(),
  alertType: z.string().min(1).optional(),
  alertName: z.string().min(1).optional(),
  sensorId: z.string().min(1).optional(),
  from: day.optional(),
  to: day.optional(),
});

export type AlertHistorySearch = z.infer<typeof alertHistorySearchSchema>;
